<template>
  <div class="equipament-photos-main">
    <Toast :style="{ width: $isMobile() ? '100vw' : '' }" />
    <Dialog
      id="dialog-equipament-photos"
      :visible.sync="show"
      :style="{ width: $isMobile() ? '100vw' : '50vw', position: 'relative' }"
      v-bind:class="[{ 'p-dialog-maximized': $isMobile() }]"
      :modal="true"
      :closable="false"
      :header="'Adicionar Ficheiros ao Pat ' + patId"
    >
      <Loading
        :active.sync="loading"
        :isFullPage="false"
        :zIndex="9000"
      ></Loading>

      <div class="p-fluid p-formgrid p-grid">
        <div class="p-col-12">
          <AppFileUpload
            ref="fileUpload"
            mode="advanced"
            name="files[]"
            chooseLabel="Escolher Ficheiros"
            :multiple="true"
            :showUploadButton="false"
            :showCancelButton="false"
            :previewWidth="200"
            @select="onFilesSelect"
            @remove="onFileRemove"
          >
            <template v-slot:fileContent="{ file, index }">
              <div class="file-description-wrapper">
                <div class="p-field">
                  <span class="p-float-label">
                    <InputText
                      :id="'description-' + index"
                      v-model="getFileEntry(file).description"
                      :maxlength="80"
                      :class="{
                        'p-invalid': getFileEntry(file).descriptionError
                      }"
                      @input="onDescriptionInput(getFileEntry(file))"
                    />
                    <label :for="'description-' + index">Descrição *</label>
                  </span>
                  <small
                    v-if="getFileEntry(file).descriptionError"
                    class="p-error"
                    role="alert"
                  >
                    Descrição é obrigatória
                  </small>
                </div>

                <div class="p-d-flex p-jc-end">
                  <Button
                    label="Ler imagem"
                    icon="pi pi-search"
                    class="p-button-text p-button-sm p-button-info"
                    :disabled="!canScanFile(file) || ocrLoading"
                    :loading="ocrLoading && isCurrentOcrFile(file)"
                    @click="readImage(file)"
                  />
                </div>
              </div>
            </template>
          </AppFileUpload>
        </div>
      </div>

      <template #footer>
        <Button
          :label="selectedFiles.length > 0 ? 'Guardar (' + selectedFiles.length + ')' : 'Guardar'"
          icon="pi pi-save"
          class="p-button-success"
          @click="sendPhotos"
          :disabled="loading || selectedFiles.length === 0"
        />
        <Button
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-danger"
          @click="close"
          :disabled="loading"
        />
      </template>
    </Dialog>

    <Dialog
      :visible.sync="showOcrDialog"
      :style="{ width: $isMobile() ? '100vw' : '48rem' }"
      v-bind:class="[{ 'p-dialog-maximized': $isMobile() }]"
      :modal="true"
      :closable="!ocrLoading"
      header="Leitura OCR"
    >
      <div v-if="ocrFileEntry" class="ocr-dialog-content">
        <div v-if="ocrOptions.length" class="p-field">
          <label class="ocr-section-label">Selecione o serial number</label>
          <div class="ocr-options-list">
            <button
              v-for="(option, index) in ocrOptions"
              :key="`${index}-${option}`"
              type="button"
              class="ocr-option-card"
              :class="{ 'ocr-option-card-active': ocrSelectedOption === option }"
              @click="selectOcrOption(option)"
            >
              {{ option }}
            </button>
          </div>
        </div>

        <div class="ocr-body-grid">
          <div class="ocr-preview-card">
            <div class="ocr-preview-frame">
              <img
                v-if="ocrPreviewUrl"
                :src="ocrPreviewUrl"
                :alt="ocrFileEntry.file.name"
                class="ocr-preview-image"
              />
            </div>
            <small class="ocr-file-name">{{ ocrFileEntry.file.name }}</small>
          </div>

          <div class="ocr-text-card">
            <div class="p-field">
              <label for="ocr-result" class="ocr-section-label">Descrição a aplicar</label>
              <Textarea
                id="ocr-result"
                v-model="ocrText"
                :maxlength="80"
                rows="8"
                autoResize
                class="p-inputtext-sm ocr-textarea"
              />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          label="Usar na descrição"
          icon="pi pi-check"
          class="p-button-success"
          :disabled="!ocrFileEntry || !ocrText.trim()"
          @click="applyOcrText"
        />
        <Button
          label="Fechar"
          icon="pi pi-times"
          class="p-button-secondary"
          :disabled="ocrLoading"
          @click="closeOcrDialog"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import patService from "@/services/pat.service";
import filesService from "@/services/files.service";
import AppFileUpload from "@/components/elements/AppFileUpload.vue";

export default {
  name: "AddFiles",
  props: ["show", "patId"],
  components: { Loading, AppFileUpload },
  watch: {
    show(newVal) {
      if (newVal === false) {
        this.reset();
      }
    },
  },
  data() {
    return {
      loading: false,
      selectedFiles: [],
      showOcrDialog: false,
      ocrLoading: false,
      ocrFileEntry: null,
      ocrPreviewUrl: null,
      ocrText: "",
      ocrOptions: [],
      ocrSelectedOption: null,
    };
  },
  methods: {
    getFileKey(file) {
      return file ? `${file.name}-${file.size}` : null;
    },
    limitDescription(value) {
      return (value || "").toString().slice(0, 80);
    },
    getFileEntry(file) {
      let entry = this.selectedFiles.find(
        entry => entry.file.name === file.name && entry.file.size === file.size
      );

      if (!entry) {
        entry = {
          file: file,
          description: null,
          descriptionError: false,
        };
        this.selectedFiles.push(entry);
      }

      return entry;
    },
    onDescriptionInput(entry) {
      entry.description = this.limitDescription(entry.description);
      entry.descriptionError = false;
    },
    onFilesSelect(event) {
      const newFiles = event.files;

      newFiles.forEach(file => {
        const exists = this.selectedFiles.some(entry =>
          entry.file.name === file.name &&
          entry.file.size === file.size
        );

        if (!exists) {
          this.selectedFiles.push({
            file: file,
            description: null,
            descriptionError: false,
          });
        }
      });
    },
    onFileRemove() {
      this.selectedFiles = this.selectedFiles.filter(entry => {
        return this.$refs.fileUpload.files.some(f =>
          f.name === entry.file.name && f.size === entry.file.size
        );
      });
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    reset() {
      this.selectedFiles = [];
      this.loading = false;
      this.closeOcrDialog();
      if (this.$refs.fileUpload) {
        this.$refs.fileUpload.clear();
      }
    },
    close() {
      this.reset();
      return this.$emit("close");
    },
    isCurrentOcrFile(file) {
      if (!this.ocrFileEntry || !this.ocrFileEntry.file) {
        return false;
      }

      return this.getFileKey(this.ocrFileEntry.file) === this.getFileKey(file);
    },
    canScanFile(file) {
      return file && file.type && file.type.startsWith("image/");
    },
    setOcrPreview(file) {
      if (this.ocrPreviewUrl) {
        URL.revokeObjectURL(this.ocrPreviewUrl);
      }

      this.ocrPreviewUrl = file ? URL.createObjectURL(file) : null;
    },
    closeOcrDialog() {
      if (this.ocrPreviewUrl) {
        URL.revokeObjectURL(this.ocrPreviewUrl);
      }

      this.showOcrDialog = false;
      this.ocrLoading = false;
      this.ocrFileEntry = null;
      this.ocrPreviewUrl = null;
      this.ocrText = "";
      this.ocrOptions = [];
      this.ocrSelectedOption = null;
    },
    selectOcrOption(option) {
      this.ocrSelectedOption = option;
      this.ocrText = this.limitDescription(option);
    },
    normalizeOcrOption(option) {
      if (typeof option === "string") {
        return option.trim();
      }

      if (!option || typeof option !== "object") {
        return "";
      }

      const candidateKeys = [
        "serial_number",
        "serialNumber",
        "text",
        "description",
        "label",
        "value",
        "content",
        "title",
        "name",
      ];

      for (const key of candidateKeys) {
        if (typeof option[key] === "string" && option[key].trim()) {
          return option[key].trim();
        }
      }

      return "";
    },
    extractOcrOptions(payload) {
      if (!payload) {
        return [];
      }

      const optionKeys = [
        "options",
        "choices",
        "results",
        "suggestions",
        "texts",
        "candidates",
        "descriptions",
        "data",
      ];

      const sourceArrays = [];

      if (Array.isArray(payload)) {
        sourceArrays.push(payload);
      }

      if (typeof payload === "object") {
        optionKeys.forEach(key => {
          if (Array.isArray(payload[key])) {
            sourceArrays.push(payload[key]);
          }
        });
      }

      const normalizedOptions = sourceArrays.reduce((accumulator, options) => {
        const values = options
          .map(option => this.normalizeOcrOption(option))
          .filter(Boolean);

        return accumulator.concat(values);
      }, []);

      return [...new Set(normalizedOptions)];
    },
    hasValidOcrResult(response) {
      const status = response?.data?.status;
      const options = this.extractOcrOptions(response?.data);

      if (status && status !== "success" && options.length === 0) {
        return false;
      }

      return true;
    },
    async readImage(file) {
      const entry = this.getFileEntry(file);

      if (!this.canScanFile(file)) {
        this.$toast.add({
          severity: "warn",
          summary: "OCR",
          detail: "Só é possível ler imagens com OCR.",
          life: 3000,
        });
        return;
      }

      this.ocrFileEntry = entry;
      this.ocrLoading = true;

      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await filesService.scanFileOcr(formData);
        const recognizedOptions = this.extractOcrOptions(response?.data);
        const recognizedText = this.hasValidOcrResult(response) && response?.text
          ? response.text.trim()
          : "";

        if (!recognizedText && !recognizedOptions.length) {
          this.$toast.add({
            severity: "warn",
            summary: "OCR",
            detail: "Não foi possível identificar texto na imagem.",
            life: 3000,
          });
          return;
        }

        if (recognizedOptions.length) {
          this.ocrText = this.limitDescription(recognizedOptions[0]);
          this.ocrOptions = recognizedOptions;
          this.ocrSelectedOption = recognizedOptions[0];
          this.setOcrPreview(file);
          this.showOcrDialog = true;
          return;
        }

        entry.description = this.limitDescription(recognizedText);
        entry.descriptionError = false;

        this.$toast.add({
          severity: "success",
          summary: "OCR",
          detail: "Descrição preenchida automaticamente.",
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Ocorreu um erro ao ler a imagem com OCR.",
          life: 3000,
        });
      } finally {
        this.ocrLoading = false;
      }
    },
    applyOcrText() {
      if (!this.ocrFileEntry || !this.ocrText.trim()) {
        return;
      }

      this.ocrFileEntry.description = this.limitDescription(this.ocrText.trim());
      this.ocrFileEntry.descriptionError = false;
      this.closeOcrDialog();
    },
    validateAll() {
      let isValid = true;

      this.selectedFiles.forEach(entry => {
        if (!entry.description || entry.description.trim() === "") {
          entry.descriptionError = true;
          isValid = false;
        } else {
          entry.descriptionError = false;
        }
      });

      this.$forceUpdate();

      return isValid;
    },
    sendPhotos() {
      if (this.selectedFiles.length === 0) {
        this.$toast.add({
          severity: "error",
          summary: "Validação",
          detail: "Selecione pelo menos um ficheiro",
          life: 3000,
        });
        return;
      }

      if (!this.validateAll()) {
        this.$toast.add({
          severity: "error",
          summary: "Validação",
          detail: "Por favor, preencha a descrição de todos os ficheiros",
          life: 3000,
        });
        return;
      }

      this.savePhotos();
    },
    async savePhotos() {
      this.loading = true;

      try {
        const uploadPromises = this.selectedFiles.map(entry => {
          const formData = new FormData();
          formData.append("description", entry.description);
          formData.append("file", entry.file);
          return patService.addFile(this.patId, formData);
        });

        const responses = await Promise.all(uploadPromises);

        const allSuccessful = responses.every(response => response);

        if (!allSuccessful) {
          this.loading = false;
          return this.$toast.add({
            severity: "error",
            summary: "Erro",
            detail: "Ocorreu um erro ao guardar alguns ficheiros",
            life: 3000,
          });
        }

        this.loading = false;
        this.$toast.add({
          severity: "success",
          summary: "Sucesso",
          detail: `${responses.length} ficheiro(s) guardado(s) com sucesso`,
          life: 3000,
        });

        this.reset();
        return this.$emit("addedFile", responses);
      } catch (error) {
        this.loading = false;
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Ocorreu um erro ao guardar os ficheiros",
          life: 3000,
        });
      }
    },
  },
};
</script>

<style scoped>
.p-fileupload {
  width: 100% !important;
}

.equipament-photos-main > img {
  width: 150px !important;
  height: auto !important;
}

.file-description-wrapper {
  width: 100%;
  margin-top: 0.75rem;
}

.ocr-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ocr-body-grid {
  display: grid;
  grid-template-columns: 12rem minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.ocr-preview-card,
.ocr-text-card {
  border: 1px solid #d7dee7;
  border-radius: 10px;
  background: #f8fafc;
  padding: 0.875rem;
}

.ocr-preview-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 10rem;
  max-height: 12rem;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.ocr-preview-image {
  max-width: 100%;
  max-height: 11rem;
  object-fit: contain;
  display: block;
}

.ocr-file-name {
  display: block;
  margin-top: 0.75rem;
  color: #5b6472;
  word-break: break-word;
}

.ocr-section-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #243447;
}

.ocr-options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  max-height: 11rem;
  overflow-y: auto;
}

.ocr-option-card {
  appearance: none;
  border: 1px solid #c8d3df;
  background: #ffffff;
  color: #243447;
  border-radius: 999px;
  padding: 0.55rem 0.9rem;
  cursor: pointer;
  font-size: 0.875rem;
  line-height: 1.2;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ocr-option-card:hover,
.ocr-option-card:focus {
  border-color: #3b82f6;
  outline: none;
}

.ocr-option-card-active {
  background: #eaf3ff;
  border-color: #3b82f6;
  color: #0f4c81;
}

.ocr-textarea {
  width: 100%;
  min-height: 9rem;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .file-description-wrapper {
    padding: 0 0.5rem;
  }

  .ocr-body-grid {
    grid-template-columns: 1fr;
  }

  .ocr-preview-frame {
    min-height: 8rem;
  }
}
</style>
